import { useFormContext } from 'react-hook-form';
import { InputTextArea, type InputTextAreaProps } from '../InputTextArea';
import { CharactersCounter, ErrorMessage, FormField } from './FormField';
import { ruleDescriptionSchema } from './schema';

const fieldName = 'description';
const fieldLabel = ruleDescriptionSchema.describe().label;
const maxChars = ruleDescriptionSchema
  .describe()
  .tests.find((constraint) => constraint.name === 'max')?.params?.max as number;

export type RuleDescriptionFieldProps = Pick<
  InputTextAreaProps,
  'rows' | 'readOnly'
> & {
  hideLabel?: boolean;
};

const ReadOnlyRuleDescriptionField = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="p-5 flex rounded-md bg-ds-natural-50 h-20">
    <div className="text-ds-natural-600 font-ds-inter text-ds-400 font-ds-normal tracking-ds-normal leading-ds-300 break-words  overflow-auto">
      {children}
    </div>
  </div>
);

export const RuleDescriptionField = ({
  rows = 8,
  readOnly,
  hideLabel,
}: RuleDescriptionFieldProps) => {
  const {
    register,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();
  const state = errors[fieldName] ? 'error' : 'normal';
  const value = watch(fieldName);

  if (readOnly)
    return <ReadOnlyRuleDescriptionField>{value}</ReadOnlyRuleDescriptionField>;

  return (
    <FormField
      name={fieldName}
      label={!hideLabel && fieldLabel}
    >
      <InputTextArea
        {...register(fieldName)}
        state={state}
        rows={rows}
        onChange={(e) => {
          setValue(fieldName, e.target.value, {
            shouldDirty: true,
          });
          trigger(fieldName);
        }}
        placeholder="Add rule description"
      />
      <div className="flex leading-ds-200">
        <ErrorMessage errors={errors} />
        <div className="ml-auto">
          <CharactersCounter
            current={value?.length}
            max={maxChars}
          />
        </div>
      </div>
    </FormField>
  );
};
